// Generic interface for template-like objects with keywords
interface ITemplateWithKeywords {
	keywords?: string[];
}

/**
 * Filters out templates with the "LevDraft" keyword for regular users.
 * Shows all templates when user is impersonating a customer account.
 *
 * @param templates - Array of template cards to filter
 * @param isCustomerImpersonation - Whether the user is currently impersonating a customer
 * @returns Filtered array of templates
 */
export const filterLevDraftTemplates = <T extends ITemplateWithKeywords>(
	templates: T[],
	isCustomerImpersonation?: boolean
): T[] => {
	if (isCustomerImpersonation) {
		// Show all templates when impersonating
		return templates;
	}

	// Filter out templates with "LevDraft" keyword for regular users (case-insensitive)
	return templates.filter(template => !hasLevDraftKeyword(template));
};

/**
 * Checks if a template has the "LevDraft" keyword (case-insensitive)
 */
export const hasLevDraftKeyword = (template: ITemplateWithKeywords): boolean => {
	return template.keywords?.some(keyword => keyword.toLowerCase() === 'levdraft') ?? false;
};

/**
 * Checks if a template has any keyword starting with "ccc" prefix (case-insensitive).
 * Templates with CCC keywords are part of the Custom Content Calendar and
 * have additional date fields (suggestedDate, expirationDate) that can be edited.
 */
export const hasCccKeyword = (template: ITemplateWithKeywords): boolean => {
	return template.keywords?.some(keyword => keyword.toLowerCase().startsWith('ccc')) ?? false;
};

/**
 * Determines if a template should be shown based on LevDraft keyword and impersonation status
 */
export const shouldShowTemplate = (template: ITemplateWithKeywords, isCustomerImpersonation?: boolean): boolean => {
	if (!hasLevDraftKeyword(template)) {
		return true; // Always show non-draft templates
	}
	return isCustomerImpersonation === true; // Only show drafts during impersonation
};
